"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.registerPageRoutes = exports.registerRouteConfig = exports.getIdFromRouteParams = exports.getRoutesByPageNameLocale = exports.getRouteByPageNameLocale = exports.getRouteByNextRouter = exports.getRouteByLangId = exports.getRoutesById = exports.getRouteByPageTypeLocale = exports.getRoutesByPageType = exports.matchAllRoutes = exports.matchRoute = exports.__cleanup = exports.getRoutePageProp = exports.unzipRouteProp = exports.zipRouteProp = exports.ROUTE_PAGE_PROP = exports.fixNextHistoryState = exports.addPathLocale = exports.removePathLocale = void 0;
const trimChars_1 = __importDefault(require("lodash/fp/trimChars"));
const path_to_regexp_1 = require("path-to-regexp");
function removePathLocale(path, locale) {
    return path.replace(new RegExp(`^/${locale}(/|$)`), '/');
}
exports.removePathLocale = removePathLocale;
function addPathLocale(path, locale) {
    return `/${locale}${path}`.replace(/\/$/, '');
}
exports.addPathLocale = addPathLocale;
function fixNextHistoryState(data, url) {
    const { url: stateUrl, as, options: { locale }, } = data;
    if (!locale || !as || typeof url !== 'string')
        return [data, url];
    const routePageUrl = new URL(stateUrl, 'http://a');
    const route = getRouteByPageNameLocale(routePageUrl.pathname, locale);
    if (!route) {
        if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.error('Could not find route to fix locale prefix', routePageUrl.pathname, locale);
        }
        return [data, url];
    }
    const hasPrefix = Boolean(route.prefix);
    if (hasPrefix)
        return [data, url];
    return [
        Object.assign(Object.assign({}, data), { as: removePathLocale(as, locale) }),
        removePathLocale(url, locale),
    ];
}
exports.fixNextHistoryState = fixNextHistoryState;
/**
 * Monkey patching of window pushState and replaceState methods.
 * It needs for client only problem with language prefixes that are added by Next.js always when `locale` is passed.
 * It needs to get rid of locale prefix when the route should not have it in address bar, e.g. /ferien/suchen
 * Some of the routes are already localized, some others are not.
 * There is `prefix` boolean in RouteConfig object for each configured route.
 */
if (typeof window === 'object' && typeof window.history === 'object') {
    const originalPushState = window.history.pushState.bind(window.history);
    const originalReplaceState = window.history.replaceState.bind(window.history);
    window.history.pushState = (data, _, url) => {
        const [fixedData, fixedUrl] = fixNextHistoryState(data, url);
        return originalPushState(fixedData, _, fixedUrl);
    };
    window.history.replaceState = (data, _, url) => {
        const [fixedData, fixedUrl] = fixNextHistoryState(data, url);
        return originalReplaceState(fixedData, _, fixedUrl);
    };
}
exports.ROUTE_PAGE_PROP = '_rout';
function zipRouteProp(prop) {
    const { locale, pathname, searchParams, params, routeId, origin } = prop;
    const url = new URL(`${pathname}?${searchParams}`, origin);
    return [
        routeId,
        locale,
        url.toString(),
        new URLSearchParams(params).toString(),
    ];
}
exports.zipRouteProp = zipRouteProp;
function unzipRouteProp(zippedProp) {
    if (!zippedProp)
        return undefined;
    const [routeId, locale, urlString, paramsString] = zippedProp;
    const { origin, pathname, searchParams } = new URL(urlString);
    const params = Object.fromEntries(new URLSearchParams(paramsString));
    return {
        origin,
        routeId,
        locale,
        pathname,
        searchParams: searchParams.toString(),
        params,
    };
}
exports.unzipRouteProp = unzipRouteProp;
function getRoutePageProp(pageProps) {
    var _a, _b;
    if (typeof pageProps !== 'object')
        return;
    return unzipRouteProp((_b = (_a = pageProps['pageProps']) === null || _a === void 0 ? void 0 : _a[exports.ROUTE_PAGE_PROP]) !== null && _b !== void 0 ? _b : pageProps[exports.ROUTE_PAGE_PROP]);
}
exports.getRoutePageProp = getRoutePageProp;
let routes = [];
let routesByPageType = {};
function __cleanup() {
    routes = [];
    routesByPageType = {};
}
exports.__cleanup = __cleanup;
function mapConfigToRoute(config) {
    const originalBuilder = (0, path_to_regexp_1.compile)(config.match);
    const keys = [];
    const regex = (0, path_to_regexp_1.pathToRegexp)(config.match, keys);
    function builder(props) {
        const path = originalBuilder(props);
        return config.prefix
            ? `/${config.locale}${path === '/' ? '' : path}`
            : path;
    }
    // freeze changing route params
    return Object.freeze(Object.assign(Object.assign({}, config), { domain: config.domain || null, matcher: (0, path_to_regexp_1.match)(config.match), keys,
        regex,
        builder }));
}
/**
 * Match any pathname along with PageType to IRoute
 * it should be configured with `registerRouteConfig` or `registerPageRoutes`
 * PageType is needed due to similar route match expressions.
 *
 * @param pathname Any pathname, query string is ignored
 * @param pageType PageType
 */
function matchRoute(pathname, pageType) {
    const [pathOnly] = pathname.split('?');
    return routes.find(route => (typeof pageType === 'undefined' || route.pageType === pageType) &&
        route.regex.test(pathOnly));
}
exports.matchRoute = matchRoute;
function matchAllRoutes(pathname, preCondition) {
    const [pathOnly] = pathname.split('?');
    return routes.filter(route => (!preCondition || preCondition(route)) && route.regex.test(pathOnly));
}
exports.matchAllRoutes = matchAllRoutes;
/**
 * Get an array of routes by pageType
 * They might be similar routes with different languages
 * or routes that shares pageType between each other (that should be avoided)
 *
 * @param pageType PageType
 * @return IRoute[] Routes array
 */
function getRoutesByPageType(pageType) {
    return routesByPageType[pageType];
}
exports.getRoutesByPageType = getRoutesByPageType;
/**
 * Get route by pair of pageType and locale string
 *
 * @param pageType PageType
 * @param locale Locale string
 * @return IRoute? Route
 */
function getRouteByPageTypeLocale(pageType, locale) {
    var _a;
    return (_a = getRoutesByPageType(pageType)) === null || _a === void 0 ? void 0 : _a.find(route => route.locale === locale);
}
exports.getRouteByPageTypeLocale = getRouteByPageTypeLocale;
/**
 * Get routes array by generic route ID
 * These routes will be the same config set but with different locales.
 * An ID could be generated with `getIdFromRouteParams`
 *
 * @param id Route ID
 * @return IRoute[] Routes array
 */
function getRoutesById(id) {
    return id ? routes.filter(route => route.id === id) : undefined;
}
exports.getRoutesById = getRoutesById;
/**
 * Get single route by generic route ID and locale string.
 * An ID could be generated with `getIdFromRouteParams`
 *
 * @param id Route ID
 * @param locale Route ID
 * @return IRoute Route
 */
function getRouteByLangId(id, locale) {
    var _a;
    return (_a = getRoutesById(id)) === null || _a === void 0 ? void 0 : _a.find(route => route.locale === locale);
}
exports.getRouteByLangId = getRouteByLangId;
/**
 * Normalize NextRoute to a single route config
 *
 * @param nextRoute NextRoute object (asPath and route strings are required)
 * @return IRoute Route
 */
function getRouteByNextRouter(nextRoute) {
    var _a, _b;
    const { asPath, route: page, query: { locale = ((_a = nextRoute.query) === null || _a === void 0 ? void 0 : _a.locale) ||
        ((_b = nextRoute.query) === null || _b === void 0 ? void 0 : _b.nextInternalLocale) ||
        nextRoute.locale, } = {}, } = nextRoute;
    const [path] = asPath.split('?');
    const indexPage = page.replace(/\/$/, '') + '/index';
    return routes.find(route => (route.page === page || route.page === indexPage) &&
        (!locale || route.locale === locale) &&
        (route.regex.exec(path) ||
            (locale && route.regex.exec(`/${locale}${path}`))));
}
exports.getRouteByNextRouter = getRouteByNextRouter;
function getRouteByPageNameLocale(page, locale) {
    return routes.find(route => route.page === page && route.locale === locale);
}
exports.getRouteByPageNameLocale = getRouteByPageNameLocale;
function getRoutesByPageNameLocale(page, locale) {
    return routes.filter(route => route.page === page && route.locale === locale);
}
exports.getRoutesByPageNameLocale = getRoutesByPageNameLocale;
/**
 * Generate Route ID from route config params (pageType and page)
 *
 * @param config Route config object
 * @return string Route ID
 */
function getIdFromRouteParams(config) {
    const { page, pageType } = config;
    const pageVariant = config.pageVariant ? `_${config.pageVariant}` : '';
    return `${pageType}${pageVariant}_${(0, trimChars_1.default)('/')(page)}`.toLowerCase();
}
exports.getIdFromRouteParams = getIdFromRouteParams;
/**
 * Register new Route by it's config object,
 * it does override the same route with new one if it's already exist.
 *
 * @param config
 * @return IRoute Normalized route
 */
function registerRouteConfig(config) {
    const id = getIdFromRouteParams(config);
    const existingRoute = getRouteByLangId(id, config.locale);
    const route = mapConfigToRoute(Object.assign(Object.assign({}, config), { id }));
    if (existingRoute) {
        replaceRoute(existingRoute, route);
    }
    else {
        addNewRoute(route);
    }
    return route;
}
exports.registerRouteConfig = registerRouteConfig;
/**
 * Utility function to register an array of page route configurations.
 * It does use `registerRouteConfig` to register each.
 *
 * @param configs Array of route configs
 * @return IRoute[]
 */
function registerPageRoutes(configs) {
    return configs.map(registerRouteConfig);
}
exports.registerPageRoutes = registerPageRoutes;
function replaceRoute(prevOne, newOne) {
    const index = routes.indexOf(prevOne);
    const indexByType = routesByPageType[prevOne.pageType].indexOf(prevOne);
    routes[index] = newOne;
    routesByPageType[prevOne.pageType][indexByType] = newOne;
}
function addNewRoute(newOne) {
    routes.push(newOne);
    if (!routesByPageType[newOne.pageType])
        routesByPageType[newOne.pageType] = [];
    routesByPageType[newOne.pageType].push(newOne);
}
//# sourceMappingURL=index.js.map